import { useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Loader2, XCircle } from "lucide-react"

import { SiteHeader } from "@/components/ui-kit"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { Card } from "@/components/ui-kit/card"
import { usePayment } from "@/hooks/payments/usePayments"
import { getPaymentFailureMessage } from "@/lib/payment-messages"
import type { ApiError } from "@/types/api"

export function PaymentPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const paymentId = searchParams.get("paymentId")

  const { data: payment, isLoading, isError, error } = usePayment(paymentId, true)

  const status = payment?.status
  // A payment apostle does not know is not one that failed.
  const isUnknown = !paymentId || (isError && (error as ApiError | null)?.code === "404")
  const isPending = isLoading || status === "pending"
  const isFailed = !isUnknown && (status === "failed" || status === "canceled" || isError)

  useEffect(() => {
    if (status === "succeeded" && paymentId) {
      navigate(`/payments/done?paymentId=${paymentId}`, { replace: true })
    }
  }, [status, paymentId, navigate])

  return (
    <div className="min-h-screen bg-background sm:pt-20 pt-12">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[722px] px-2 pt-18 pb-16 md:px-4">
        <Card className="relative flex flex-col items-center px-6 py-12 text-center">
          {isPending && (
            <>
              <Loader2 className="mb-6 size-16 animate-spin text-brand" />
              <h1 className="mb-2 text-[28px] leading-[36px] font-semibold text-foreground">
                Проверяем оплату
              </h1>
              <p className="text-lg leading-6 text-muted-foreground">
                Подождите, мы получаем подтверждение от платёжной системы
              </p>
            </>
          )}

          {isFailed && (
            <>
              <XCircle className="mb-6 size-20 text-[#df0f0f]" />
              <h1 className="mb-2 text-[28px] leading-[36px] font-semibold text-foreground">
                Оплата не прошла
              </h1>
              <p className="mb-8 text-lg leading-6 text-muted-foreground">
                {getPaymentFailureMessage(payment?.error)}
              </p>
              <UiKitButton type="button" className="w-full max-w-[400px]" onClick={() => navigate("/home")}>
                Вернуться к боксам
              </UiKitButton>
            </>
          )}

          {isUnknown && !isLoading && (
            <>
              <XCircle className="mb-6 size-20 text-[#df0f0f]" />
              <h1 className="mb-2 text-[28px] leading-[36px] font-semibold text-foreground">
                Платёж не найден
              </h1>
              <p className="mb-8 text-lg leading-6 text-muted-foreground">
                Не удалось определить платёж. Вернитесь в личный кабинет и попробуйте снова.
              </p>
              <UiKitButton type="button" className="w-full max-w-[400px]" onClick={() => navigate("/home")}>
                Вернуться к боксам
              </UiKitButton>
            </>
          )}
        </Card>
      </main>
    </div>
  )
}
